import { hasClientSession, logoutSession } from '../services/session';
import { passwordPolicyError, PASSWORD_HELP } from '../utils/passwordPolicy';
// audit-safety-integrated
import { readJson, safeErrorMessage } from '../utils/auditSafety';
import React, { useState } from 'react';
import { API_BASE_URL } from '../services/api';


const Settings = () => {
  // Password change states
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Handle logout and redirect
  const handleLogout = async () => {
    try {
      await logoutSession();
      sessionStorage.setItem('loginMessage', 'Please sign in again with your new password');
      window.location.replace('/login');
    } catch (error) {
      setMessage({ text: 'Password changed, but sign-out failed. Please use the Sign out button to retry.', type: 'error' });
    }
  };

  // Handle password change
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (loading) return;

    // Reset message
    setMessage({ text: '', type: '' });

    // Validate input
    if (!oldPassword || !newPassword || !confirmPassword) {
      setMessage({ text: 'All fields are required', type: 'error' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ text: 'New passwords do not match', type: 'error' });
      return;
    }

    const policyError = passwordPolicyError(newPassword);
    if (policyError || newPassword === oldPassword) {
      setMessage({ text: policyError || 'Choose a password different from your current password.', type: 'error' });
      return;
    }
    // Submit password change
    try {
      setLoading(true);

      const sessionAvailable = hasClientSession();
      if (!sessionAvailable) {
        throw new Error('Your session is unavailable.');
      }

      const response = await fetch(`${API_BASE_URL}/user/password/change`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          old: oldPassword,
          new: newPassword
        })
      });

      const data = response.status === 204 ? {} : await readJson(response);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      // Success message with countdown
      let countdown = 3;
      setMessage({
        text: `Password changed successfully. You will be redirected to login in ${countdown} seconds.`,
        type: 'success'
      });

      // Clear form
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Create countdown timer with visual feedback
      const countdownInterval = setInterval(() => {
        countdown -= 1;

        if (countdown <= 0) {
          clearInterval(countdownInterval);
          // Force logout immediately after countdown
          handleLogout();
        } else {
          // Update message with new countdown
          setMessage({
            text: `Password changed successfully. You will be redirected to login in ${countdown} seconds.`,
            type: 'success'
          });
        }
      }, 1000);

    } catch (error) {
      setMessage({ text: safeErrorMessage(error) || 'Error changing password', type: 'error' });
      void 0;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-700">Change Password</h2>
        </div>

        <div className="p-6">
          {message.text && (
            <div className={`mb-4 p-3 rounded-md ${
              message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
              'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          <p id="password-policy" className="mb-4 text-sm text-gray-600">{PASSWORD_HELP}</p>
          <form onSubmit={handleChangePassword}>
            <div className="space-y-4">
              <div>
                <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  id="oldPassword"
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter current password"
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter new password"
                  minLength={12}
                  maxLength={128}
                  autoComplete="new-password"
                  aria-describedby="password-policy"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm new password"
                  minLength={12}
                  maxLength={128}
                  autoComplete="new-password"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    loading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Changing Password...
                    </span>
                  ) : 'Change Password'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
